// FAQ category filter (FAQ-01)
//
// Accessible single-select pill toggle-group that shows/hides the FAQ accordion
// rows by category. Mirrors the vanilla web-component shape of accordion-custom.js
// (no framework, AbortController cleanup, guarded customElements.define).
//
// Markup contract (rendered by sections/faq.liquid):
//   <faq-filter>
//     <div class="faq-pills" role="group" aria-label="...">
//       <button type="button" class="faq-pill" data-category="<handle>" aria-pressed="true|false">…</button>
//       …
//     </div>
//     <div class="accordion …">
//       <accordion-custom class="faq-section__row" data-category="<handle>">…</accordion-custom>
//       …
//     </div>
//   </faq-filter>
//
// Responsibilities (ONLY filtering — accordion open/close stays in accordion-custom.js):
//   - exactly one pill is aria-pressed="true" at a time (single-select)
//   - the pressed pill's category is the only one whose rows are visible (row.hidden)
//   - rows render WITHOUT `hidden` in HTML (no-JS = all visible); the active filter
//     is applied on connect, so JS users see the filtered view immediately
//   - roving tabindex: the pressed pill is the single tab-stop; ArrowLeft/ArrowRight
//     move focus between pills AND select the focused pill (W3C APG toggle-group)
//   - pill labels never change on toggle (state conveyed via aria-pressed + accent)

class FaqFilter extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #pills = [];

  /** @type {HTMLElement[]} */
  #rows = [];

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#pills = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.faq-pill')]);
    this.#rows = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('accordion-custom[data-category]')]);

    // No pills (e.g. no categories assigned yet) → leave every row visible, no-op.
    if (this.#pills.length === 0) return;

    for (const pill of this.#pills) {
      pill.addEventListener('click', () => this.select(pill), { signal });
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    // Apply the active filter on load (first pressed pill, fallback to the first pill).
    const active = this.#pills.find((p) => p.getAttribute('aria-pressed') === 'true') || this.#pills[0];
    this.select(active);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Single-selects a pill: marks it aria-pressed, makes it the roving tab-stop,
   * and shows only the rows whose data-category matches the pill.
   * @param {HTMLButtonElement} pill - The pill to activate.
   */
  select(pill) {
    const category = pill.dataset.category;

    for (const p of this.#pills) {
      const isActive = p === pill;
      p.setAttribute('aria-pressed', String(isActive));
      // Roving tabindex: only the active pill is in the tab order.
      p.tabIndex = isActive ? 0 : -1;
    }

    for (const row of this.#rows) {
      row.hidden = row.dataset.category !== category;
    }
  }

  /**
   * ArrowLeft/ArrowRight: roving focus between pills + select the focused pill.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const currentIndex = this.#pills.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (currentIndex < 0) return;

    event.preventDefault();

    const count = this.#pills.length;
    const nextIndex =
      event.key === 'ArrowRight'
        ? (currentIndex + 1) % count
        : (currentIndex - 1 + count) % count;

    const nextPill = this.#pills[nextIndex];
    this.select(nextPill);
    nextPill.focus();
  };
}

if (!customElements.get('faq-filter')) {
  customElements.define('faq-filter', FaqFilter);
}
